import { useEffect, useState } from 'react';
import {
  FusionHCIObject,
  FusionHCISoftwareObject,
  FusionHCISoftwareTypes,
  FusionHCITypes,
  FusionHardwareObject,
  FusionHardwareTypes,
  GibMemorySize,
  HCI,
  HCISoftware,
  PremiumSupport,
  Size,
  vCPUSize,
} from './defaults';
import { InputType } from './data';
import { ROUND, ROUNDUP } from './Formatters';

export class FusionHCIData {
  totalAnnualSalary = 0;
  totalMaintenanceSupportCurrent = 0;
  totalSoftwareCurrent = 0;
  totalHardwareCurrent = 0;
  totalCurrent = 0;
  totalSavings = 0;
  totalAnnualAdminCostIBM = 0;
  totalMaintenanceSupportIBM = 0;
  totalIBMFusionCosts = 0;
  totalHardwareCosts = 0;
  totalIBMSoftwareCosts = 0;
  totalIBM = 0;
  nbrServersCurrent = 0;

  totalvCPU = (input: InputType) => {
    const inputvCPU: Size = {
      X_small: input.xSmallSize * vCPUSize.X_small,
      Small: input.smallSize * vCPUSize.Small,
      Medium: input.mediumSize * vCPUSize.Medium,
      Large: input.largeSize * vCPUSize.Large,
      X_large: input.xLargeSize * vCPUSize.X_large,
    };
    let totalvCPU = 0;
    Object.entries(inputvCPU).forEach(([key, value]) => {
      totalvCPU += value;
    });
    return totalvCPU;
  };

  averageTotal = () => {
    const usableNVMe = FusionHCIObject.filter(
      (item: HCI) => item.label === FusionHCITypes.Usable_NVMe,
    )[0];
    const workerNodes = FusionHCIObject.filter(
      (item: HCI) => item.label === FusionHCITypes.Worker_nodes,
    )[0];
    const controlNodes = FusionHCIObject.filter(
      (item: HCI) => item.label === FusionHCITypes.Control_nodes,
    )[0];

    const averageCapacity: HCI = {
      label: FusionHCITypes.average_capacity,
      E03: usableNVMe.E03 / (workerNodes.E03 + controlNodes.E03),
      E04: usableNVMe.E04 / (workerNodes.E04 + controlNodes.E04),
      E05: usableNVMe.E05 / (workerNodes.E05 + controlNodes.E05),
      E06: usableNVMe.E06 / (workerNodes.E06 + controlNodes.E06),
      E07: usableNVMe.E07 / (workerNodes.E07 + controlNodes.E07),
      E08: usableNVMe.E08 / (workerNodes.E08 + controlNodes.E08),
      E09: usableNVMe.E09 / (workerNodes.E09 + controlNodes.E09),
      E10: usableNVMe.E10 / (workerNodes.E10 + controlNodes.E10),
      E11: usableNVMe.E11 / (workerNodes.E11 + controlNodes.E11),
    };

    let totalCapacity = 0,
      totalCapacityCount = 0;
    Object.entries(averageCapacity).forEach(([key, value]) => {
      if (typeof value == 'number') {
        totalCapacity += value;
        totalCapacityCount++;
      }
    });

    return totalCapacity / totalCapacityCount;
  };

  averageCostPerVCPuTotal = () => {
    const licenseVCPu = FusionHCISoftwareObject.filter(
      (item: HCISoftware) => item.label === FusionHCISoftwareTypes.License_VCPu,
    )[0];
    const listPrice = FusionHCISoftwareObject.filter(
      (item: HCISoftware) => item.label === FusionHCISoftwareTypes.List_Price,
    )[0];
    const discount = FusionHCISoftwareObject.filter(
      (item: HCISoftware) => item.label === FusionHCISoftwareTypes.Discount,
    )[0];

    const discountedPrice: HCISoftware = {
      label: FusionHCISoftwareTypes.Discount,
      E03: listPrice.E03 * (1 - discount.E03 / 100),
      E05: listPrice.E05 * (1 - discount.E05 / 100),
      E07: listPrice.E07 * (1 - discount.E07 / 100),
      E09: listPrice.E09 * (1 - discount.E09 / 100),
      E11: listPrice.E11 * (1 - discount.E11 / 100),
    };

    const averageCostPerVCPu: HCISoftware = {
      label: FusionHCISoftwareTypes.Average_Cost_per_VCP,
      E03: listPrice.E03 / licenseVCPu.E03,
      E05: listPrice.E05 / licenseVCPu.E05,
      E07: listPrice.E07 / licenseVCPu.E07,
      E09: listPrice.E09 / licenseVCPu.E09,
      E11: listPrice.E11 / licenseVCPu.E11,
    };

    let _averageCostPerVCPuTotal = 0,
      _totalAverageCostPerVCPuTotal = 0;
    Object.entries(averageCostPerVCPu).forEach(([key, value]) => {
      if (typeof value == 'number') {
        _averageCostPerVCPuTotal += value;
        _totalAverageCostPerVCPuTotal++;
      }
    });
    return _averageCostPerVCPuTotal / _totalAverageCostPerVCPuTotal;
  };

  totalGibMemory = (input: InputType) => {
    const listPriceFusionHardware = FusionHardwareObject.filter(
      (item: HCISoftware) => item.label === FusionHardwareTypes.List_Price,
    )[0];
    const discountFusionHardware = FusionHardwareObject.filter(
      (item: HCISoftware) => item.label === FusionHardwareTypes.Discount,
    )[0];
    const totalInThousand = FusionHardwareObject.filter(
      (item: HCISoftware) =>
        item.label === FusionHardwareTypes.Total_in_thousands,
    )[0];
    const totalWorkerNodes = FusionHardwareObject.filter(
      (item: HCISoftware) =>
        item.label === FusionHardwareTypes.Total_Worker_Nodes,
    )[0];
    const controlNodes = FusionHCIObject.filter(
      (item: HCI) => item.label === FusionHCITypes.Control_nodes,
    )[0];

    const totalFusionHardware: HCISoftware = {
      label: FusionHardwareTypes.Total,
      E03: listPriceFusionHardware.E03 * (1 - discountFusionHardware.E03),
      E05: listPriceFusionHardware.E05 * (1 - discountFusionHardware.E05),
      E07: listPriceFusionHardware.E07 * (1 - discountFusionHardware.E07),
      E09: listPriceFusionHardware.E09 * (1 - discountFusionHardware.E09),
      E11: listPriceFusionHardware.E11 * (1 - discountFusionHardware.E11),
    };

    const shipping: HCISoftware = {
      label: FusionHardwareTypes.Shipping,
      E03: totalInThousand.E03 - totalFusionHardware.E03,
      E05: totalInThousand.E05 - totalFusionHardware.E05,
      E07: totalInThousand.E07 - totalFusionHardware.E07,
      E09: totalInThousand.E09 - totalFusionHardware.E09,
      E11: totalInThousand.E11 - totalFusionHardware.E11,
    };

    const totalNodes: HCISoftware = {
      label: FusionHardwareTypes.Total_Nodes,
      E03: totalWorkerNodes.E03 + controlNodes.E03,
      E05: totalWorkerNodes.E05 + controlNodes.E05,
      E07: totalWorkerNodes.E07 + controlNodes.E07,
      E09: totalWorkerNodes.E09 + controlNodes.E09,
      E11: totalWorkerNodes.E11 + controlNodes.E11,
    };

    const hardwareCostPerNode: HCISoftware = {
      label: FusionHardwareTypes.Hardware_Cost_per_Node,
      E03: listPriceFusionHardware.E03 / totalNodes.E03,
      E05: listPriceFusionHardware.E05 / totalNodes.E05,
      E07: listPriceFusionHardware.E07 / totalNodes.E07,
      E09: listPriceFusionHardware.E09 / totalNodes.E09,
      E11: listPriceFusionHardware.E11 / totalNodes.E11,
    };
    const inputGibMemory: Size = {
      X_small: input.xSmallSize * GibMemorySize.X_small,
      Small: input.smallSize * GibMemorySize.Small,
      Medium: input.mediumSize * GibMemorySize.Medium,
      Large: input.largeSize * GibMemorySize.Large,
      X_large: input.xLargeSize * GibMemorySize.X_large,
    };

    let totalGibMemory = 0;
    Object.entries(inputGibMemory).forEach(([key, value]) => {
      totalGibMemory += value;
    });
    const CostPerNodeIBM = hardwareCostPerNode.E11;

    return { totalGibMemory, CostPerNodeIBM };
  };

  coreOverheadIBM = () => {
    const availablevCPU = FusionHCIObject.filter(
      (item: HCI) => item.label === FusionHCITypes.Available_vCPU,
    )[0];
    const totalvCPU1 = FusionHCIObject.filter(
      (item: HCI) => item.label === FusionHCITypes.Total_vCPU,
    )[0];

    const coreOverheadIBM = availablevCPU.E03 / totalvCPU1.E03;
    return coreOverheadIBM;
  };

  public Calculate({
    input,
    isNbrAdminChanged,
    setInputType,
  }: {
    input: InputType;
    isNbrAdminChanged?: boolean;
    setInputType: React.Dispatch<React.SetStateAction<InputType>>;
  }) {
    const vCPUs = ROUND(this.totalvCPU(input) / input.totalVMs, 1);
    const VMmemory = ROUND(
      this.totalGibMemory(input).totalGibMemory / input.totalVMs,
      1,
    );
    const cores = ROUND(vCPUs * input.totalVMs, 1);
    const memory = ROUND(VMmemory * input.totalVMs, 1);

    //With IBM
    const vCPUperHCIIBM =
      FusionHCIObject.filter(
        (item: HCISoftware) => item.label === FusionHCITypes.Available_vCPU,
      )[0].E03 /
      FusionHCIObject.filter(
        (item: HCISoftware) => item.label === FusionHCITypes.Worker_nodes,
      )[0].E03;

    const MaxWorkerNodePerRackIBM = 16;
    const workerNodesIBM = ROUNDUP(cores / vCPUperHCIIBM);
    const numberClusterIBM = ROUNDUP(
      cores / vCPUperHCIIBM / MaxWorkerNodePerRackIBM,
      0,
    );
    const controlNodesIBM = numberClusterIBM * 3;

    const CostPerNodeIBM = this.totalGibMemory(input).CostPerNodeIBM;
    const HardwareDiscountIBM = 73.0;

    const totalHardwareCosts = ROUND(
      CostPerNodeIBM *
        (1 - HardwareDiscountIBM / 100) *
        (workerNodesIBM + controlNodesIBM) *
        1000,
      0,
    );
    const costPerNodeFusion = this.averageCostPerVCPuTotal();
    const fusionDiscount = 75.0;
    const totalIBMFusionCosts = ROUND(
      costPerNodeFusion * (1 - fusionDiscount / 100) * vCPUperHCIIBM * 1000,
      0,
    );

    const IBMOpenShiftCost = 9000;
    const IBMTotalOpenShift = IBMOpenShiftCost * workerNodesIBM;
    const totalIBMSoftwareCosts = totalIBMFusionCosts + IBMTotalOpenShift;

    let _averagePremiumSupport = 0,
      _countPremiumSupport = 0;
    Object.entries(PremiumSupport).forEach(([key, value]) => {
      if (typeof value == 'number') {
        _averagePremiumSupport += value;
        _countPremiumSupport++;
      }
    });

    const averagePremiumSupport = _averagePremiumSupport / _countPremiumSupport;
    const totalMaintenanceSupportIBM = averagePremiumSupport * 1000;

    //Current calculation
    const supportAssumption = 10;

    const sanSwitchCost = 18000 + 2000;
    const nbrSwitch = 2;
    const totalSwitchCost = ROUND(sanSwitchCost * nbrSwitch, 0);
    const sanAnnualSupport = (sanSwitchCost * supportAssumption) / 100;

    const capacityPerGB = this.averageTotal() * (1 + 20 / 100) * 1024;
    const costPerGB = 1.32;
    const totalHCINodesCurrent = workerNodesIBM + controlNodesIBM;
    const storageArrayCost = ROUND(
      capacityPerGB * costPerGB * totalHCINodesCurrent,
      0,
    );
    const storageSupportCost = ROUND(
      (storageArrayCost * supportAssumption) / 100,
      0,
    );

    const nbrCoreCurrent = 56;
    const nbrOverheadCurrent = (1 - this.coreOverheadIBM()) * 100;
    const nbrvCPUCurrent = nbrCoreCurrent * (1 - nbrOverheadCurrent / 100);
    const vCPUOverheadCurrent = 20;
    const adjustedvCPUCurrent =
      nbrvCPUCurrent * (1 - vCPUOverheadCurrent / 100);
    this.nbrServersCurrent = ROUNDUP(cores / adjustedvCPUCurrent, 0);
    const costPerServerCurrent = 15000;
    const totalCostServersCurrent = ROUND(
      this.nbrServersCurrent * costPerServerCurrent,
      0,
    );
    const serverSupportCostCurrent = ROUND(
      (totalCostServersCurrent * supportAssumption) / 100,
      0,
    );

    const currentOpenShiftCores = this.nbrServersCurrent * nbrCoreCurrent;
    const currentOpenShiftCost = 1250;
    const currentTotalOpenShift = ROUND(
      currentOpenShiftCores * currentOpenShiftCost,
      0,
    );

    const costVMwareServer = 17612.4;
    const totalVMwareCost = ROUND(costVMwareServer * this.nbrServersCurrent, 0);

    const Oscore = ROUNDUP(nbrCoreCurrent / 16, 0) * this.nbrServersCurrent;
    const MSFTCost = 6155;
    const totalGuesOS =
      ROUND(Oscore * MSFTCost, 0) * (1 - input.replatform / 100);

    this.totalHardwareCurrent =
      totalSwitchCost + storageArrayCost + totalCostServersCurrent;
    this.totalSoftwareCurrent =
      totalGuesOS + totalVMwareCost + currentTotalOpenShift;
    this.totalMaintenanceSupportCurrent =
      sanAnnualSupport + storageSupportCost + serverSupportCostCurrent;

    //Administrative
    //Dell
    let NbrAdmin = input.NbrAdmin;
    if (!isNbrAdminChanged) {
      NbrAdmin = ROUNDUP(this.nbrServersCurrent / input.maxServersAdmin, 0);
      setInputType({
        ...input,
        NbrAdmin: NbrAdmin,
      });
    }
    const salary = input.salary;
    const burden = 60;
    this.totalAnnualSalary = NbrAdmin * salary * (1 + burden / 100);

    const efficiencyFactor = 72;

    //IBM
    const adminsIBM = NbrAdmin * (1 - efficiencyFactor / 100);
    const totalAnnualAdminCostIBM = adminsIBM * salary * (1 + burden / 100);

    this.totalAnnualAdminCostIBM = totalAnnualAdminCostIBM;
    this.totalMaintenanceSupportIBM = totalMaintenanceSupportIBM;
    this.totalIBMFusionCosts = totalIBMFusionCosts;
    this.totalHardwareCosts = totalHardwareCosts;
    this.totalIBMSoftwareCosts = totalIBMSoftwareCosts;

    this.totalCurrent =
      this.totalAnnualSalary +
      this.totalMaintenanceSupportCurrent +
      this.totalSoftwareCurrent +
      this.totalHardwareCurrent;
    this.totalIBM =
      totalAnnualAdminCostIBM +
      totalMaintenanceSupportIBM +
      totalIBMSoftwareCosts +
      totalHardwareCosts;

    this.totalSavings = this.totalCurrent - this.totalIBM;
  }

  get totalCurrentCost() {
    return {
      totalAnnualSalary: ROUND(this.totalAnnualSalary),
      totalMaintenanceSupportCurrent: ROUND(
        this.totalMaintenanceSupportCurrent,
      ),
      totalSoftwareCurrent: ROUND(this.totalSoftwareCurrent),
      totalHardwareCurrent: ROUND(this.totalHardwareCurrent),
    };
  }
  get totalIBMCost() {
    return {
      totalAnnualAdminCostIBM: ROUND(this.totalAnnualAdminCostIBM),
      totalMaintenanceSupportIBM: ROUND(this.totalMaintenanceSupportIBM),
      totalIBMSoftwareCosts: ROUND(this.totalIBMSoftwareCosts),
      totalHardwareCosts: ROUND(this.totalHardwareCosts),
    };
  }

  get totalCost() {
    return {
      totalCurrent: ROUND(this.totalCurrent),
      totalIBM: ROUND(this.totalIBM),
      totalSavings: ROUND(this.totalSavings),
    };
  }

  get nbrServersCurrentValue() {
    return this.nbrServersCurrent;
  }
}
